import { DIRECTIVE_STATUS, INGEST_PATH } from '@/api/directivesApi';
import { isFileUid } from '@/utils/fileUrl';
import { hasDocument } from '@/utils/directiveDocument';

/**
 * Who is offered "Remove" on a stored file, and the reason sent with it.
 *
 * keyon decides (keyon/lib/StoredFileDelete.php, DeleteDirectiveDocument,
 * DeleteMessageAttachment); these gates only keep the button away from people
 * keyon would refuse. A removal is a soft delete: the file stops being
 * available to everyone, and keyon keeps it for record retention.
 */

// keyon's delete_reason column holds 255 characters.
export const DELETE_REASON_MAX = 255;
// keyon/lib/UploadRefusal.php reason_required, word for word.
export const DELETE_REASON_REQUIRED_TEXT = 'Say why you are removing this document.';
// keyon/lib/StoredFileDelete.php DIRECTIVE_PRE_SIGNATURE: a signed or closed
// record is a final legal record and keeps its document.
export const DIRECTIVE_DOCUMENT_DELETABLE_STATUSES = [
  DIRECTIVE_STATUS.UNCONFIRMED_AUTHORIZATION,
  DIRECTIVE_STATUS.PENDING_REVIEW,
];
// keyon SqlClass SoftDeleteDirectiveDocument closes the submission (revoked) in
// the same transaction as the removal. A pending upload with no document could
// be neither signed nor confirmed, yet would hold the patient's one in-flight
// slot and refuse every new upload. Closed, uploading again works, so that is
// what the page says before and after.
export const DIRECTIVE_DOCUMENT_REMOVE_NOTE =
  'Removing it also closes this submission. To go on, upload the document again as a new submission.';
export const DIRECTIVE_DOCUMENT_REMOVED_TEXT =
  'The document was removed (retained for audit) and the submission was closed. Upload the document again as a new submission.';

/** @returns {{ok:true, reason:string}|{ok:false, error:string}} */
export function validateDeleteReason(text) {
  const reason = String(text == null ? '' : text).trim();
  if (reason === '') return { ok: false, error: DELETE_REASON_REQUIRED_TEXT };
  return { ok: true, reason: reason.slice(0, DELETE_REASON_MAX) };
}

/**
 * Offer the remove on a pre-signature upload's document to its uploader, or to
 * a holder of directive upload and modify (53). keyon also requires that holder
 * to have read scope for the patient, which a viewer of this patient's page has.
 */
export function canDeleteDirectiveDocument(rec, { viewerUid, canUploadModify } = {}) {
  if (!rec || typeof rec !== 'object') return false;
  if (rec.ingest_path !== INGEST_PATH.UPLOAD) return false;
  if (!hasDocument(rec) || !isFileUid(rec.document_file_uid)) return false;
  if (!DIRECTIVE_DOCUMENT_DELETABLE_STATUSES.includes(rec.status)) return false;
  if (canUploadModify === true) return true;
  const viewer = viewerUid == null ? '' : String(viewerUid);
  return viewer !== '' && String(rec.document_uploaded_by_id || '') === viewer;
}

/** Only the message's sender, only a live uid, never an item already removed. */
export function canDeleteMessageAttachment(message, item, viewerUid) {
  const viewer = viewerUid == null ? '' : String(viewerUid);
  if (viewer === '' || !message || typeof message !== 'object') return false;
  if (!item || typeof item !== 'object' || item.removed === true || !isFileUid(item.file_uid)) return false;
  return String(message.sender_id == null ? '' : message.sender_id) === viewer;
}
